/**
 * The browser side of an approval link that has to run before Angular (engine docs/integrations/connect-api.md,
 * "Approval link"). No Angular import: main.ts calls it first thing.
 */

export const INTEGRATION_APPROVE_PATH = '/integrations/approve';
export const INTEGRATION_CALLBACK_PATH = '/integrations/callback';

export const APPROVAL_TOKEN_KEY = 'kabeen.integrationApproval.token';
export const APPROVAL_FLOW_KEY = 'kabeen.integrationApproval.flow';
export const APPROVAL_CONFIRMATION_KEY = 'kabeen.integrationApproval.confirmation';

/**
 * The pages of a connection: the approval link's, and the provider's callback (its query carries a single-use code).
 * No analytics, session recording nor third-party script on them.
 */
export function isIntegrationFlowPage(pathname: string): boolean {
	return pathname === INTEGRATION_APPROVE_PATH || pathname === INTEGRATION_CALLBACK_PATH;
}

/**
 * Moves an approval link's token from the URL fragment to this tab's sessionStorage, and clears it from the address
 * bar, before anything (Angular, analytics, an error report) reads the URL. A link opened again with another token
 * starts over: the previous flow of this tab is dropped.
 */
export function captureApprovalToken(location: Location = window.location, history: History = window.history, storage: Storage = window.sessionStorage): void {
	if (location.pathname !== INTEGRATION_APPROVE_PATH || location.hash.length <= 1) {
		return;
	}
	const token = safeDecode(location.hash.slice(1));
	history.replaceState(history.state, '', location.pathname + location.search);
	try {
		if (storage.getItem(APPROVAL_TOKEN_KEY) !== token) {
			storage.removeItem(APPROVAL_FLOW_KEY);
			storage.removeItem(APPROVAL_CONFIRMATION_KEY);
		}
		storage.setItem(APPROVAL_TOKEN_KEY, token);
	} catch {
		// No sessionStorage: the page then says the link cannot be read.
	}
}

function safeDecode(value: string): string {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}
